import React, { ReactElement, ReactNode, useCallback } from "react";
import { graphql, useQueryLoader } from "react-relay";
import { ServerSearchListbox } from "@olivahealth/oli-ui";
import { debounce } from "lodash";
import { SessionSelectorQuery as SessionSelectorQueryType } from "./__generated__/SessionSelectorQuery.graphql";

interface Option<T> {
  id: string | number;
  value: T;
  label: string | ReactElement;
  icon?: ReactNode;
}
const SessionSelectorQuery = graphql`
  query SessionSelectorQuery($search: String) {
    sessionsSearch(first: 20, search: $search) {
      nodes {
        id
        status
        startDate
        employee {
          name
          surname
        }
        professional {
          name
          surname
        }
      }
    }
  }
`;

interface Props {
  onSessionSelected: (sessionId: string) => void;
  placeholder?: string;
}

// Utility function to map sessions to dropdown options
const mapSessionsToDropdown = (
  sessions:
    | SessionSelectorQueryType["response"]["sessionsSearch"]["nodes"]
    | undefined,
): Array<Option<string>> => {
  if (!sessions) return [];

  return sessions.map((session) => {
    const employeeName = session.employee
      ? `${session.employee.name} ${session.employee.surname}`
      : "Unknown";
    const professionalName = session.professional
      ? `${session.professional.name} ${session.professional.surname}`
      : "Unknown";
    const date = new Date(session.startDate).toLocaleDateString();

    return {
      id: session.id,
      value: session.id,
      label: `${employeeName} - ${professionalName} (${session.status}) - ${date}`,
    };
  });
};

export default function SessionSelector({
  onSessionSelected,
  placeholder,
}: Props): JSX.Element {
  const [sessionsQueryReference, loadSessionsQuery] =
    useQueryLoader<SessionSelectorQueryType>(SessionSelectorQuery);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadSessionsQuery = useCallback(
    debounce(loadSessionsQuery, 500),
    [loadSessionsQuery],
  );

  return (
    <ServerSearchListbox
      query={SessionSelectorQuery}
      queryReference={sessionsQueryReference}
      transformDataToOptions={(data) => {
        const typedData = data as SessionSelectorQueryType["response"];
        return mapSessionsToDropdown(typedData?.sessionsSearch?.nodes);
      }}
      onSearchChange={(inputText) => {
        if (!inputText) {
          return;
        }
        debouncedLoadSessionsQuery({
          search: inputText,
        });
      }}
      setSelected={(option) => {
        if (!option?.value) return;
        onSessionSelected(option.value);
      }}
      placeholder={placeholder ?? "Search sessions by name or ID"}
    />
  );
}
